import React from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {RadioButton} from '@optimaxdev/design-system';
import {DayType} from '@optimaxdev/utils';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {Price} from 'components/price/price';
import {CHECKOUT_DELIVERY_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';
import {ShippingMethodType} from 'types/shippingMethodsType';

import {SHIPPING_METHODS_CODES} from '../../constants/deliveryStep';
import {updateShippingMethod} from '../../store/reducers/deliveryStep/shippingMethods/shippingMethods';
import {saveDataForDeliveryStepWithoutValid} from '../../store/reducers/sagaStep/sagaStep';
import {getNextDayShippingMethod} from '../../store/selectors/shippingMethods/shippingMethods';
import {ShippingMethodsWrapper} from '../shippingMethodsWrapper/shippingMethodsWrapper';
import {ShippingTwoDaysWrapper} from '../shippingTwoDaysWrapper/shippingTwoDaysWrapper';

import s from './shippingMethodItem.scss';

export type PropsType = {
    item: ShippingMethodType;
    currentShippingMethod: string;
    setCurrentShippingMethod: (code: string) => void;
    saveCurrentShippingMethod: () => void;
    twoDayDeliveryDay: DayType | 'tomorrow' | null | undefined;
    ref: (node?: Element | null | undefined) => void;
    index: number;
};

// This is just for eslint max-statements, need to be removed later
const getShippingMethodItemStates = (item: ShippingMethodType, currentShippingMethod: string) => {
    const isActive = item.code === currentShippingMethod;

    return {...item, isActive};
};

/**
 * Shipping Method Item for methods list
 */
export const ShippingMethodItem: React.FC<PropsType> = ({
    currentShippingMethod,
    index,
    item,
    ref,
    saveCurrentShippingMethod,
    setCurrentShippingMethod,
    twoDayDeliveryDay,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {isOneStepAvailable} = useIsOneStepAvailable();

    const selectShippingMethod = (code: string, description: string) => {
        setCurrentShippingMethod(code);
        sendDesktopAnalyticsEvent(CHECKOUT_DELIVERY_STEP, {
            message:
                code === SHIPPING_METHODS_CODES.NEXT_DAY
                    ? `1 business day - ${twoDayDeliveryDay === 'tomorrow' ? 'Tomorrow' : 'Other'}`
                    : description,
        });
        if (isOneStepAvailable && currentShippingMethod !== code) {
            saveCurrentShippingMethod();
        }
    };

    const {code, description, isActive, price, title} = getShippingMethodItemStates(
        item,
        currentShippingMethod,
    );
    const itemRef = index === 0 ? {ref} : {};
    const selectMethod = () => selectShippingMethod(code, description);
    const deliveryPrice = Number(price) === 0 ? 'Free' : <Price>{price}</Price>;

    if (code === SHIPPING_METHODS_CODES.NEXT_DAY) {
        return (
            <ShippingTwoDaysWrapper
                deliveryPrice={deliveryPrice}
                isActive={isActive}
                item={item}
                itemRef={itemRef}
                selectMethod={selectMethod}
            />
        );
    }

    return (
        <button
            className={classNames(s.item, {
                [s.active]: isActive,
                [s.itemFacelift]: isFacelift,
                [s.itemRedesignB]: twoDayDeliveryDay,
                [s.oneStep]: isOneStepAvailable,
            })}
            key={code}
            onClick={selectMethod}
            type="button"
            {...itemRef}
        >
            <RadioButton isChecked={isActive} size={isFacelift ? 'medium' : 'small'} value={code} />
            <ShippingMethodsWrapper
                deliveryPrice={deliveryPrice}
                description={description}
                title={title}
            />
        </button>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    twoDayDeliveryCode: getNextDayShippingMethod(store),
});

const mapDispatchToProps = {
    saveCurrentShippingMethod: saveDataForDeliveryStepWithoutValid,
    setCurrentShippingMethod: updateShippingMethod,
};

export const ShippingMethodItemConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(ShippingMethodItem);
